import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { cValidators } from '../../../../models/validator';
import { authenticationPreferences } from '../../../../finish/models/authentication-preferences-list';
import { LoadingService } from '../../../../shared/loading/loading.service';
import { FinishProService } from '../../../services/finish-pro.service';
import { AuthService, OperationService } from '../../../../api';
import { MessageService } from '../../../../shared/message/message.service';
import { ProfileService } from '../../../../core/profile/profile.service';
import { Environment } from '../../../../models/environment';
import { PersonalizationsService } from '../../../../base/services/personalizations/personalizations.service';
import { TranslateService } from '../../../../core/translation';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

export interface IFormFields {
  key: string;
  label: string;
  placeholder: string;
  required: boolean;
  type: string;
}

@Component({
  selector: 'app-authorization-modal',
  templateUrl: './authorization-modal.component.html',
  styleUrls: ['./authorization-modal.component.scss']
})
export class AuthorizationModalComponent implements OnInit, OnDestroy {
  environment: Environment;
  authForm: FormGroup = new FormGroup({});
  authPreference: string;
  authInfo: IFormFields;
  customFieldsForm: FormGroup = new FormGroup({});
  customFields: any;
  showErrorMessage: boolean = false;
  authenticationPreferences = authenticationPreferences;

  destroy$: Subject<boolean> = new Subject<boolean>();

  constructor(
    public dialogRef: MatDialogRef<AuthorizationModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private _loading: LoadingService,
    private _finishPro: FinishProService,
    private _auth: AuthService,
    private _message: MessageService,
    private _operation: OperationService,
    private _profile: ProfileService,
    private _translate: TranslateService,
    private _personalization: PersonalizationsService
  ) {
    this.environment = this._personalization.sendEnv;
  }

  ngOnInit(): void {
    this.authPreference = this.data?.authPreference[0]?.codeAuth;
    this.createAuthForm();
  }

  ngOnDestroy(): void {
    this.destroy$.unsubscribe();
  }

  createAuthForm(): void {
    const authenticationData = {
      [authenticationPreferences.NIF]: {
        validator: Validators.compose([Validators.required, cValidators.nifNie]),
        label: this._translate.instant('finish_label_nif'),
        placeholder: 'E.g. 43218812J'
      },
      [authenticationPreferences.CIF]: {
        validator: Validators.compose([Validators.required, cValidators.cif]),
        label: this._translate.instant('finish_label_cif'),
        placeholder: 'E.g. 43218812J'
      },
      [authenticationPreferences.Tlf]: {
        validator: Validators.compose([Validators.required, cValidators.fullPhone]),
        label: this._translate.instant('registro_form_phone'),
        placeholder: ''
      },
      [authenticationPreferences.TlfMail]: {
        validator: Validators.compose([Validators.required, cValidators.phoneOrMail]),
        label: this._translate.instant('finish_label_tlfmail'),
        placeholder: ''
      },
      [authenticationPreferences.OTP]: {
        validator: Validators.compose([Validators.required, cValidators.OTP]),
        label: this._translate.instant('finish_label_otp'),
        placeholder: ''
      }
    };

    this.authInfo = {
      key: this.authPreference,
      label: authenticationData[this.authPreference]?.label,
      type: 'text',
      placeholder: authenticationData[this.authPreference]?.placeholder,
      required: true
    }

    this.authForm.addControl(
      this.authPreference,
      new FormControl('', authenticationData[this.authPreference]?.validator)
    )

    if (this.authPreference === authenticationPreferences.Custom) {
      const customFields = this.data.authPreference[0]?.customFields;
      this.createCustomFieldsForm(customFields);
    }

    if (this.authPreference === authenticationPreferences.OTP) {
      this.sendOTP();
    }
  }

  createCustomFieldsForm(customFields: any): void {
    this.customFields = [];
    for (const customField of customFields) {
      const field = {
        key: customField.keyCustomField,
        label: customField.labelCustomField,
        type: 'text',
        placeholder: '',
        required: customField.requiredCustomField
      }

      const validators = customField.requiredCustomField ? [Validators.required] : [];
      this.customFieldsForm.addControl(
        field.key,
        new FormControl('', validators)
      )

      this.customFields.push(field);
    }
  }

  setInputValue(value: any, key: string, form: FormGroup): void {
    form.patchValue({ [key]: value });
    this.showErrorMessage = false;
  }

  closeModal(): void {
    this.dialogRef.close({ isOperationAuthorized: false });
  }

  sendOTP(): void {
    const token = this._profile.getPublicToken();
    const operationId = this._finishPro.operationId;
    const appid = this.environment.appid;
    this._loading.set(true);
    this._operation.operationGetAuthOperationOTP(operationId, appid, token)
      .pipe(takeUntil(this.destroy$)).subscribe(
        (response) => {
          if (response) this._loading.set(false);
        },
        (error) => {
          this._loading.set(false);
          this._message.showError(error);
        }
      );
  }

  onSubmit(): void {
    if (!this.authForm.valid || !this.customFieldsForm?.valid) {
      this.showErrorMessage = true;

      return;
    }

    this._loading.set(true);

    let customValues = [];
    if (this.customFields?.length) {
      customValues = Object.entries(this.customFieldsForm.value).map(([key, value]) => ({
        keyAuth: key,
        valueAuth: value
      }));
    }

    const values = Object.entries(this.authForm.value).map(([key, value]) => ({
      keyAuth: key,
      valueAuth: value
    }));

    const auth = {
      idOperation: this._finishPro.operationId,
      listValues: this.customFields?.length ? customValues : values
    }
    this._auth.apiOperationAuthOperationPost('password', JSON.stringify(auth))
      .pipe(takeUntil(this.destroy$)).subscribe(
        (response) => {
          this._finishPro.authToken = `Bearer ${response.access_token}`;
          this.dialogRef.close({ isOperationAuthorized: true });
          this._loading.set(false);
        }, (error) => {
          this._loading.set(false);
          this._message.showError(error);
        });
  }
}
